import React from "react"

import { OrganizationSwitcher } from "@clerk/clerk-react"
import { dark } from "@clerk/themes"
import Box from "@mui/material/Box"
import { useTheme, alpha } from "@mui/material/styles"

import ErrorBoundary from "@components/ErrorBoundary"
import { useThemeContext } from "@providers/AppThemeProvider"

import { neutral } from "@constants/colors"
import routes from "@constants/routes"
import * as shadows from "@constants/shadows"

const OrgSwitcher: React.FC = () => {
  const { darkMode } = useThemeContext()
  const theme = useTheme()

  if (import.meta.env.REACT_APP_OFFLINE) {
    return null
  }

  return (
    <ErrorBoundary>
      <Box
        sx={{
          p: 0,
          m: 0,
          width: "100%",
          display: "flex",
          "> .cl-rootBox.cl-organizationSwitcher-root": {
            width: "100%",
            padding: theme.spacing(0),
          },
        }}
      >
        <OrganizationSwitcher
          afterSelectOrganizationUrl={routes.DASHBOARD}
          appearance={{
            baseTheme: darkMode ? dark : undefined,
            layout: {
              animations: false,
            },
            elements: {
              organizationPreview: {
                width: "100%",
                borderRadius: theme.shape.borderRadius,
                padding: theme.spacing(1),
                gap: theme.spacing(1),
              },
              organizationSwitcherTrigger: {
                width: "100%", // to fit into the sidebar even when the org name is long
                padding: theme.spacing(0),
                border: "1px solid transparent!important",
                background: "transparent",
                marginBottom: theme.spacing(-0.5), // Pull down so invisible state appears with 16px gap

                "&:hover": {
                  background: darkMode
                    ? alpha(neutral.white, 0.1)
                    : alpha(neutral.black, 0.05),
                },

                "&:active": {
                  background: darkMode
                    ? alpha(neutral.white, 0.05)
                    : alpha(neutral.black, 0.1),
                },
              },
              organizationSwitcherTriggerIcon: {
                margin: theme.spacing(0, 1, 0, 0),
                color: "inherit",
                opacity: theme.palette.opacity.helper,
                width: theme.spacing(2),
                height: theme.spacing(2),
              },
              organizationPreviewTextContainer: {
                flexFlow: "row",
                gap: theme.spacing(1),
                alignItems: "baseline",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              },
              organizationPreviewMainIdentifier: {
                fontSize: theme.typography.button.fontSize,
                fontWeight: theme.typography.button.fontWeight,
                lineHeight: theme.typography.button.lineHeight,
                color: theme.palette.text.primary,
              },
              organizationPreviewSecondaryIdentifier: {
                color: "inherit",
                opacity: theme.palette.opacity.helper,
              },
              organizationSwitcherPopoverCard: {
                maxWidth: theme.spacing(27.5),
                padding: theme.spacing(0.5),
                border: "none!important",
                background: theme.palette.background.paper,
                boxShadow: shadows.outer.lg,
                outline: `1px solid ${theme.palette.divider}`,
                outlineOffset: "-1px",
                borderRadius: theme.shape.borderRadius,
              },
              organizationSwitcherPopoverMain: {
                border: "0",
                boxShadow: "none",
                background: "none",
                borderRadius: "0",
              },
              organizationSwitcherPreviewButton: {
                padding: theme.spacing(0, 1, 0, 0),
                boxShadow: "none",
                color: theme.palette.text.secondary,
                borderRadius: theme.shape.borderRadius * 0.5,
                border: "1px solid transparent!important",
                background: "transparent",

                "&:hover": {
                  background: darkMode
                    ? alpha(neutral.white, 0.1)
                    : alpha(neutral.black, 0.05),
                },

                "&:active": {
                  background: darkMode
                    ? alpha(neutral.white, 0.05)
                    : alpha(neutral.black, 0.1),
                },
              },
              organizationSwitcherPopoverActionButton: {
                display: "none",
              },
              organizationSwitcherPopoverActions: {
                gap: theme.spacing(0.5),

                '> div[aria-label="List of all organization memberships"]': {
                  maxHeight: "50vh",
                  padding: "0",
                  borderTop: 0,
                },
              },
              organizationSwitcherPopoverActionButton__createOrganization: {
                visibility: "hidden",
                "max-height": 0,
                "min-height": "unset",
                padding: "unset",
              },
              avatarBox: {
                height: theme.spacing(2),
                width: theme.spacing(2),
                background: neutral[100],
                borderRadius: theme.shape.borderRadius * 0.5,
                border: theme.palette.divider,
                borderWidth: "1px",
                borderStyle: "solid",
              },
              avatarImage: {
                maxHeight: theme.spacing(2),
                maxWidth: theme.spacing(2),
              },
            },
          }}
          hidePersonal
        />
      </Box>
    </ErrorBoundary>
  )
}

export default OrgSwitcher
